// services/grossStockService.js

/**
 * Gross Stock Service
 * Handles all API calls related to Gross Stock and Gross Stock Transactions
 */

/**
 * Get all items with current stock
 * @param {Object} filters - Optional filters
 * @param {string} filters.metal - Filter by metal type
 * @param {string} filters.search - Search by item name or ID
 * @returns {Promise<Array>} List of items with stock
 */
export const getGrossStock = async (filters = {}) => {
    try {
        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "getGrossStock",
                origin: window.location.origin,
                ...filters
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to get gross stock');
        }
    } catch (error) {
        console.error('Error getting gross stock:', error);
        throw error;
    }
};

/**
 * Get stock summary (optionally grouped by metal)
 * @param {Object} options
 * @param {boolean} options.groupByMetal - Group items by metal type
 * @returns {Promise<Array>} Stock summary
 */
export const getStockSummary = async (options = {}) => {
    try {
        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "getStockSummary",
                origin: window.location.origin,
                data: {
                    groupByMetal: options.groupByMetal || false
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to get stock summary');
        }
    } catch (error) {
        console.error('Error getting stock summary:', error);
        throw error;
    }
};

/**
 * Get current stock for a specific item
 * @param {string} itemId - The ID of the item
 * @returns {Promise<Object>} Current stock (pieces and weight)
 */
export const getCurrentStock = async (itemId) => {
    try {
        if (!itemId) {
            throw new Error('Item ID is required');
        }

        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "getCurrentStock",
                origin: window.location.origin,
                data: {
                    itemId: itemId
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to get current stock');
        }
    } catch (error) {
        console.error('Error getting current stock:', error);
        throw error;
    }
};

/**
 * Add a new item to Gross Stock
 * @param {Object} itemData - Item details
 * @param {string} itemData.itemName - Name of the item
 * @param {string} itemData.metal - Metal type (Silver, Gold, etc.)
 * @param {boolean} itemData.isPieceBased - Whether item is tracked by pieces
 * @returns {Promise<Object>} Created item
 */
export const addGrossStockItem = async (itemData) => {
    try {
        // Validate required fields
        if (!itemData.itemName) {
            throw new Error('Item name is required');
        }

        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "addGrossStock",
                origin: window.location.origin,
                data: {
                    itemName: itemData.itemName,
                    metal: itemData.metal || '',
                    isPieceBased: itemData.isPieceBased || false
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to add item');
        }
    } catch (error) {
        console.error('Error adding gross stock item:', error);
        throw error;
    }
};

/**
 * Update an existing item in Gross Stock
 * @param {string} itemId - The ID of the item to update
 * @param {Object} itemData - Updated item details
 * @param {string} itemData.itemName - Name of the item
 * @param {string} itemData.metal - Metal type
 * @param {boolean} itemData.isPieceBased - Whether item is tracked by pieces
 * @returns {Promise<Object>} Updated item
 */
export const updateGrossStockItem = async (itemId, itemData) => {
    try {
        if (!itemId) {
            throw new Error('Item ID is required');
        }

        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "updateGrossStock",
                origin: window.location.origin,
                data: {
                    id: itemId,
                    itemName: itemData.itemName,
                    metal: itemData.metal,
                    isPieceBased: itemData.isPieceBased
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to update item');
        }
    } catch (error) {
        console.error('Error updating gross stock item:', error);
        throw error;
    }
};

/**
 * Delete an item from Gross Stock
 * Note: Item can only be deleted if it has no transactions
 * @param {string} itemId - The ID of the item to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteGrossStockItem = async (itemId) => {
    try {
        if (!itemId) {
            throw new Error('Item ID is required');
        }

        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "deleteGrossStock",
                origin: window.location.origin,
                data: {
                    id: itemId
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to delete item');
        }
    } catch (error) {
        console.error('Error deleting gross stock item:', error);
        throw error;
    }
};

/**
 * Add a stock transaction (add or subtract stock)
 * @param {Object} transactionData - Transaction details
 * @param {string} transactionData.itemId - ID of the item
 * @param {string} transactionData.operation - 'add' or 'subtract'
 * @param {number} transactionData.numberOfPieces - Number of pieces (0 for weight-based)
 * @param {number} transactionData.weightChanged - Weight in grams (0 for piece-based)
 * @param {string} transactionData.partyName - Party name (customer/supplier)
 * @param {string} transactionData.description - Transaction description
 * @returns {Promise<Object>} Created transaction
 */
export const addStockTransaction = async (transactionData) => {
    try {
        // Validate required fields
        if (!transactionData.itemId) {
            throw new Error('Item ID is required');
        }
        if (!transactionData.operation) {
            throw new Error('Operation is required (add or subtract)');
        }
        if (!['add', 'subtract'].includes(transactionData.operation)) {
            throw new Error('Operation must be "add" or "subtract"');
        }

        // Validate quantity
        const pieces = parseFloat(transactionData.numberOfPieces) || 0;
        const weight = parseFloat(transactionData.weightChanged) || 0;
        if (pieces === 0 && weight === 0) {
            throw new Error('Either pieces or weight must be greater than 0');
        }

        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "addGrossStockTransaction",
                origin: window.location.origin,
                data: {
                    itemId: transactionData.itemId,
                    operation: transactionData.operation,
                    numberOfPieces: pieces,
                    weightChanged: weight,
                    partyName: transactionData.partyName || '',
                    description: transactionData.description || ''
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to add stock transaction');
        }
    } catch (error) {
        console.error('Error adding stock transaction:', error);
        throw error;
    }
};

/**
 * Get stock transactions with filters
 * @param {Object} filters - Filter options
 * @param {string} filters.itemId - Filter by item ID
 * @param {string} filters.operation - Filter by operation (add/subtract)
 * @param {string} filters.partyName - Filter by party name
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} List of transactions
 */
export const getStockTransactions = async (filters = {}) => {
    try {
        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "getGrossStockTransactions",
                origin: window.location.origin,
                data: {
                    itemId: filters.itemId || undefined,
                    operation: filters.operation || undefined,
                    partyName: filters.partyName || undefined,
                    startDate: filters.startDate || undefined,
                    endDate: filters.endDate || undefined
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to get stock transactions');
        }
    } catch (error) {
        console.error('Error getting stock transactions:', error);
        throw error;
    }
};

/**
 * Update a stock transaction
 * @param {string} transactionId - ID of the transaction to update
 * @param {Object} updateData - Updated transaction data
 * @param {string} updateData.itemId - New item ID
 * @param {string} updateData.operation - New operation (add/subtract)
 * @param {number} updateData.numberOfPieces - New pieces count
 * @param {number} updateData.weightChanged - New weight
 * @param {string} updateData.partyName - New party name
 * @param {string} updateData.description - New description
 * @returns {Promise<Object>} Updated transaction
 */
export const updateStockTransaction = async (transactionId, updateData) => {
    try {
        if (!transactionId) {
            throw new Error('Transaction ID is required');
        }

        // Validate quantity if provided
        if (updateData.numberOfPieces !== undefined || updateData.weightChanged !== undefined) {
            const pieces = parseFloat(updateData.numberOfPieces) || 0;
            const weight = parseFloat(updateData.weightChanged) || 0;
            if (pieces === 0 && weight === 0) {
                throw new Error('Either pieces or weight must be greater than 0');
            }
        }

        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "updateGrossStockTransaction",
                origin: window.location.origin,
                data: {
                    id: transactionId,
                    itemId: updateData.itemId,
                    operation: updateData.operation,
                    numberOfPieces: updateData.numberOfPieces,
                    weightChanged: updateData.weightChanged,
                    partyName: updateData.partyName,
                    description: updateData.description
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to update transaction');
        }
    } catch (error) {
        console.error('Error updating stock transaction:', error);
        throw error;
    }
};

/**
 * Delete a stock transaction
 * Note: Stock will be recalculated after deletion
 * @param {string} transactionId - ID of the transaction to delete
 * @returns {Promise<Object>} Deletion result with updated stock
 */
export const deleteStockTransaction = async (transactionId) => {
    try {
        if (!transactionId) {
            throw new Error('Transaction ID is required');
        }

        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "deleteGrossStockTransaction",
                origin: window.location.origin,
                data: {
                    id: transactionId
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to delete transaction');
        }
    } catch (error) {
        console.error('Error deleting stock transaction:', error);
        throw error;
    }
};

/**
 * Add multiple stock transactions in bulk
 * @param {Array} transactions - Array of transaction objects
 * @param {string} transactions[].itemId - Item ID
 * @param {string} transactions[].operation - 'add' or 'subtract'
 * @param {number} transactions[].numberOfPieces - Number of pieces
 * @param {number} transactions[].weightChanged - Weight in grams
 * @param {string} transactions[].partyName - Party name
 * @param {string} transactions[].description - Description
 * @returns {Promise<Object>} Bulk operation result
 */
export const bulkAddStockTransactions = async (transactions) => {
    try {
        if (!Array.isArray(transactions) || transactions.length === 0) {
            throw new Error('Transactions array cannot be empty');
        }

        // Validate each transaction
        const validatedTransactions = transactions.map((tx, index) => {
            if (!tx.itemId) {
                throw new Error(`Transaction ${index + 1}: Item ID is required`);
            }
            if (!tx.operation || !['add', 'subtract'].includes(tx.operation)) {
                throw new Error(`Transaction ${index + 1}: Operation must be "add" or "subtract"`);
            }
            
            const pieces = parseFloat(tx.numberOfPieces) || 0;
            const weight = parseFloat(tx.weightChanged) || 0;
            if (pieces === 0 && weight === 0) {
                throw new Error(`Transaction ${index + 1}: Either pieces or weight must be > 0`);
            }

            return {
                itemId: tx.itemId,
                operation: tx.operation,
                numberOfPieces: pieces,
                weightChanged: weight,
                partyName: tx.partyName || '',
                description: tx.description || ''
            };
        });

        const response = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            body: JSON.stringify({
                apiKey: process.env.REACT_APP_API_KEY || "DHRUVDARSHAN",
                action: "bulkAddStockTransactions",
                origin: window.location.origin,
                data: {
                    transactions: validatedTransactions
                }
            })
        });

        const result = await response.json();

        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error || 'Failed to add bulk transactions');
        }
    } catch (error) {
        console.error('Error adding bulk stock transactions:', error);
        throw error;
    }
};

/**
 * Get stock for a specific metal type
 * @param {string} metal - Metal type (Silver, Gold, etc.)
 * @returns {Promise<Array>} Items of that metal type
 */
export const getStockByMetal = async (metal) => {
    try {
        if (!metal) {
            throw new Error('Metal type is required');
        }

        return await getGrossStock({ metal });
    } catch (error) {
        console.error('Error getting stock by metal:', error);
        throw error;
    }
};

/**
 * Search items by name
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Matching items
 */
export const searchItems = async (searchTerm) => {
    try {
        if (!searchTerm || searchTerm.length < 2) {
            return await getGrossStock();
        }

        return await getGrossStock({ search: searchTerm });
    } catch (error) {
        console.error('Error searching items:', error);
        throw error;
    }
};

/**
 * Get transaction history for a specific item
 * @param {string} itemId - Item ID
 * @param {Object} options - Additional options
 * @param {number} options.limit - Limit number of transactions
 * @returns {Promise<Array>} Transaction history
 */
export const getItemTransactionHistory = async (itemId, options = {}) => {
    try {
        if (!itemId) {
            throw new Error('Item ID is required');
        }

        const filters = { itemId };
        if (options.limit) {
            filters.limit = options.limit;
        }

        const transactions = await getStockTransactions(filters);
        
        // If limit is provided, slice the array (already sorted by date desc)
        if (options.limit) {
            return transactions.slice(0, options.limit);
        }
        
        return transactions;
    } catch (error) {
        console.error('Error getting item transaction history:', error);
        throw error;
    }
};

/**
 * Get stock value summary (total pieces and weight across all items)
 * @param {string} metal - Optional: filter by metal
 * @returns {Promise<Object>} Stock value summary
 */
export const getStockValueSummary = async (metal = null) => {
    try {
        const items = await getGrossStock(metal ? { metal } : {});
        
        const summary = {
            totalPieces: 0,
            totalWeight: 0,
            totalItems: items.length,
            byMetal: {}
        };

        items.forEach(item => {
            summary.totalPieces += item.currentStock || 0;
            summary.totalWeight += item.currentWeight || 0;

            if (!summary.byMetal[item.metal]) {
                summary.byMetal[item.metal] = {
                    pieces: 0,
                    weight: 0,
                    itemCount: 0
                };
            }
            summary.byMetal[item.metal].pieces += item.currentStock || 0;
            summary.byMetal[item.metal].weight += item.currentWeight || 0;
            summary.byMetal[item.metal].itemCount += 1;
        });

        return summary;
    } catch (error) {
        console.error('Error getting stock value summary:', error);
        throw error;
    }
};

// Export all functions as a single object for convenience
const grossStockService = {
    getGrossStock,
    getStockSummary,
    getCurrentStock,
    addGrossStockItem,
    updateGrossStockItem,
    deleteGrossStockItem,
    addStockTransaction,
    getStockTransactions,
    updateStockTransaction,
    deleteStockTransaction,
    bulkAddStockTransactions,
    getStockByMetal,
    searchItems,
    getItemTransactionHistory,
    getStockValueSummary
};

export default grossStockService;